import BackgroundMusicModal from "@app/components/background-music-modal";
import ConfirmModal from "@app/components/confirm-modal";
import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  SystemMessage,
} from "@app/constants";
import BackgroundMusicService from "@app/services/http/background-music.service";
import { openPortalDialog } from "@app/services/modal.service";
import { BackgroundMusic, CreateBackgroundMusic, Meta } from "@app/types";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useState } from "react";

function LinkColumnTable({ data }: { data: BackgroundMusic }) {
  return (
    <div className="truncate min-w-[180px] max-w-[280px] text-blue-500 hover:underline">
      <a href={data.link} target="_blank" rel="noreferrer">
        {data.link}
      </a>
    </div>
  );
}

const columnConfig: ColumnConfig[] = [
  {
    label: "ID",
    dataKey: "id",
    width: "20%",
    tdClassName: "truncate min-w-[180px] max-w-[280px]",
    hasOrder: true,
  },
  {
    label: "Tên nhạc nền",
    dataKey: "name",
    width: "40%",
    tdClassName: "truncate min-w-[100px] max-w-[250px]",
    hasOrder: true,
  },
  {
    label: "File nhạc",
    dataKey: "link",
    width: "40%",
    component: LinkColumnTable,
    hasOrder: true,
  },
];

function BackgroundMusicManagement() {
  const [musics, setMusics] = useState<BackgroundMusic[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();

  const [update, forceUpdate] = useForceUpdate();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    BackgroundMusicService.getList(requestParams, abortController.signal).then(
      (res) => {
        setMusics(res.data.items);
        setMeta(res.data.meta);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }
      }
    );
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleCreateClick = () => {
    const backgroundMusicModalObs = openPortalDialog(BackgroundMusicModal);

    backgroundMusicModalObs.afterClosed().subscribe((data) => {
      if (data?.backgroundMusic) {
        const createBackgroundMusic: CreateBackgroundMusic = {
          name: data.backgroundMusic.name,
          audio: data.backgroundMusic.audio,
        };

        BackgroundMusicService.create(createBackgroundMusic).then(() => {
          addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
          forceUpdate();
        });
      }
    });
  };

  const handleEditClick = (backgroundMusic: BackgroundMusic) => {
    const backgroundMusicModalObs = openPortalDialog(BackgroundMusicModal, {
      isEdit: true,
      backgroundMusic,
    });

    backgroundMusicModalObs.afterClosed().subscribe((data) => {
      if (data?.backgroundMusic) {
        const createBackgroundMusic: CreateBackgroundMusic = {
          name: data.backgroundMusic.name,
          audio: data.backgroundMusic.audio,
        };

        BackgroundMusicService.update(
          backgroundMusic.id,
          createBackgroundMusic
        ).then(() => {
          addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
          forceUpdate();
        });
      }
    });
  };

  const handleDeleteClick = (backgroundMusicId: number) => {
    if (backgroundMusicId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_BG_MUSIC_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          BackgroundMusicService.delete(backgroundMusicId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách nhạc nền</div>
        <div>
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
            onClick={handleCreateClick}
          >
            <div className="flex gap-4">
              <img src={Images.PlusIcon.default} alt="" />
              <div>Tạo mới</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Danh sách nhạc nền"
          onInputChange={onSearchChange}
        />
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={musics}
            hasCheckBox
            hasAction
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default BackgroundMusicManagement;
